import careConfig from "@careConfig";
import { useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import PrintPreview from "@/CAREUI/misc/PrintPreview";
import Loading from "@/components/Common/Loading";
import PrintFooter from "@/components/Common/PrintFooter";
import ErrorPage from "@/components/ErrorPages/DefaultErrorPage";
import {
  MonetaryDisplay,
  getCurrencySymbol,
} from "@/components/ui/monetary-display";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { MonetaryComponentType } from "@/types/base/monetaryComponent/monetaryComponent";
import { InvoiceRead, InvoiceStatus } from "@/types/billing/invoice/invoice";
import invoiceApi from "@/types/billing/invoice/invoiceApi";
import { PaymentReconciliationStatus } from "@/types/billing/paymentReconciliation/paymentReconciliation";
import query from "@/Utils/request/query";
import { formatName, formatPatientAge } from "@/Utils/utils";

interface PrintInvoicesProps {
  facilityId: string;
  invoiceIds: string;
}

const getWatermark = (invoice: InvoiceRead, t: (key: string) => string) => {
  if (invoice.status === InvoiceStatus.cancelled) {
    return { text: t("cancelled"), color: "red" as const };
  }
  if (invoice.status === InvoiceStatus.entered_in_error) {
    return { text: t("entered_in_error"), color: "red" as const };
  }
  return undefined;
};

export default function PrintInvoices({
  facilityId,
  invoiceIds,
}: PrintInvoicesProps) {
  const { t } = useTranslation();
  const ids = Array.from(
    new Set(
      invoiceIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  );
  const invoiceQueries = useQueries({
    queries: ids.map((invoiceId) => ({
      queryKey: ["invoice", invoiceId],
      queryFn: query(invoiceApi.retrieveInvoice, {
        pathParams: { facilityId, invoiceId },
      }),
    })),
  });
  const invoices = invoiceQueries
    .map((result) => result.data)
    .filter((invoice): invoice is InvoiceRead => Boolean(invoice));
  const { facility, isFacilityLoading } = useCurrentFacility();

  if (isFacilityLoading || invoiceQueries.some((result) => result.isLoading)) {
    return <Loading />;
  }
  if (invoiceQueries.some((result) => result.isError)) {
    return <ErrorPage forError="PAGE_LOAD_ERROR" />;
  }
  if (!facility || invoices.length === 0) {
    return <Loading />;
  }

  const emailPatient = invoices.every(
    (invoice) => invoice.account.patient.id === invoices[0].account.patient.id,
  )
    ? invoices[0].account.patient
    : null;

  return (
    <PrintPreview
      title={`${t("invoices")} (${invoices.length})`}
      patientId={emailPatient?.id}
      patientEmail={emailPatient?.email}
      patientName={emailPatient?.name}
    >
      <div className="mx-auto max-w-5xl">
        {invoices.map((invoice, index) => (
          <section
            key={invoice.id}
            className={cn(index > 0 && "mt-8 break-before-page")}
          >
            {getWatermark(invoice, t) && (
              <div className="mb-3 text-center text-3xl font-bold uppercase tracking-widest text-red-600 opacity-30">
                {getWatermark(invoice, t)?.text}
              </div>
            )}
            <div className="mb-4 flex items-start justify-between border-b border-gray-200 pb-2">
              <div>
                <h1 className="text-2xl font-medium">{facility.name}</h1>
                {facility.address && (
                  <p className="whitespace-pre-wrap text-sm text-gray-500">
                    {facility.address}
                  </p>
                )}
                {facility.phone_number && (
                  <p className="text-sm text-gray-500">
                    {t("phone")}: {facility.phone_number}
                  </p>
                )}
              </div>
              <img
                src={careConfig.mainLogo?.dark}
                alt="Logo"
                className="h-10 w-auto object-contain"
              />
            </div>

            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-gray-700">
                  {t("bill_to")}:
                </div>
                <p className="font-semibold">
                  {invoice.account.patient.name}
                  <span className="ml-2 font-normal text-gray-600">
                    ({t(`GENDER__${invoice.account.patient.gender}`)},{" "}
                    {formatPatientAge(invoice.account.patient, true)})
                  </span>
                </p>
              </div>
              <div className="text-right text-sm">
                <p>
                  <span className="font-medium text-gray-700">
                    {t("invoice")}:{" "}
                  </span>
                  {invoice.number}
                </p>
                <p>
                  <span className="font-medium text-gray-700">
                    {t("issue_date")}:{" "}
                  </span>
                  {invoice.issue_date
                    ? format(
                        new Date(invoice.issue_date),
                        "dd MMM, yyyy h:mm a",
                      )
                    : "-"}
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded border border-gray-300">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead>{t("item")}</TableHead>
                    <TableHead className="text-right">
                      {t("unit_price")} ({getCurrencySymbol()})
                    </TableHead>
                    <TableHead className="text-center">{t("qty")}</TableHead>
                    <TableHead className="text-right">
                      {t("total")} ({getCurrencySymbol()})
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.charge_items.map((item, itemIndex) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-center">
                        {itemIndex + 1}
                      </TableCell>
                      <TableCell>{item.title || "-"}</TableCell>
                      <TableCell className="text-right">
                        <MonetaryDisplay
                          amount={
                            item.unit_price_components.find(
                              (component) =>
                                component.monetary_component_type ===
                                MonetaryComponentType.base,
                            )?.amount || "0"
                          }
                          hideCurrency
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        {item.quantity}
                      </TableCell>
                      <TableCell className="text-right">
                        <MonetaryDisplay
                          amount={item.total_price}
                          hideCurrency
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 ml-auto w-64 space-y-2 text-sm">
              <div className="flex justify-between font-semibold">
                <span>{t("total")}</span>
                <MonetaryDisplay amount={invoice.total_gross} />
              </div>
              <div className="flex justify-between">
                <span>{t("total_received")}</span>
                <MonetaryDisplay amount={invoice.total_payments} />
              </div>
              <div className="flex justify-between border-t pt-2 font-semibold">
                <span>{t("net_amount")}</span>
                <MonetaryDisplay amount={invoice.total_net} />
              </div>
            </div>

            {invoice.payments?.some(
              (payment) =>
                payment.status === PaymentReconciliationStatus.active,
            ) && (
              <div className="mt-5">
                <h2 className="mb-2 text-sm font-semibold">{t("payments")}</h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("date_and_time")}</TableHead>
                      <TableHead>{t("payment_method")}</TableHead>
                      <TableHead className="text-right">
                        {t("amount")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.payments
                      .filter(
                        (payment) =>
                          payment.status === PaymentReconciliationStatus.active,
                      )
                      .map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            {payment.payment_datetime
                              ? format(
                                  new Date(payment.payment_datetime),
                                  "dd MMM yyyy, h:mm a",
                                )
                              : "-"}
                          </TableCell>
                          <TableCell>{payment.method}</TableCell>
                          <TableCell className="text-right">
                            <MonetaryDisplay amount={payment.amount} />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {invoice.payment_terms && (
              <div className="mt-5 border-t pt-4">
                <h2 className="text-sm font-semibold">{t("payment_terms")}</h2>
                <p className="mt-1 text-sm">{invoice.payment_terms}</p>
              </div>
            )}
            <PrintFooter
              leftContent={
                <>
                  <span className="font-semibold">{t("created_by")}: </span>
                  {formatName(invoice.created_by)}
                </>
              }
            />
          </section>
        ))}
      </div>
    </PrintPreview>
  );
}
